import { useEffect, useState, type JSX } from 'react';
import type { StreamInfo } from '../../../core/model';
import { Dialog } from '../../components/Dialog';
import { useUiContext } from '../../context';
import { attempt, safely } from '../../safe';
import { ownTitle, useChannelTitle } from '../settings/channelTitle';
import { StreamInfoForm } from '../settings/StreamInfoForm';

/**
 * Edits a draft of the stream info; Done stores it and sends it to a running swarm stream.
 * The title belongs to the channel: while the studio has none of its own the draft starts from the
 * one ompeer.com has, and only a title that was changed here is kept, until it is published.
 */
export function EditStreamInfoDialog({ onClose }: { onClose(): void }): JSX.Element {
  const { studio } = useUiContext();
  const [draft, setDraft] = useState<StreamInfo>(() => studio.project.getState().project.channel.info);

  const channelTitle = useChannelTitle();
  // Until a title is typed, the draft shows the channel's.
  const [typed, setTyped] = useState(false);
  useEffect(() => {
    if (!typed && channelTitle !== '') setDraft((d) => (d.title === '' ? { ...d, title: channelTitle } : d));
  }, [typed, channelTitle]);
  const edit = (next: StreamInfo): void => {
    if (next.title !== draft.title) setTyped(true);
    setDraft(next);
  };

  const done = (): void => {
    onClose();
    attempt(studio, () => studio.commands.setStreamInfo({ ...draft, title: ownTitle(draft.title, channelTitle) }));
    const status = studio.runtime.getState().swarm.status;
    const onAir = status === 'live' || status === 'reconnecting';
    void safely(
      studio,
      async () => {
        await studio.updateStreamInfo();
        if (onAir) studio.notify('info', 'Stream info updated.');
      },
      'Could not update the stream info',
    );
  };

  return (
    <Dialog
      title="Edit stream info"
      onClose={onClose}
      width={620}
      footer={
        <>
          <button type="button" className="ui-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ui-btn primary" onClick={done}>
            Done
          </button>
        </>
      }
    >
      <StreamInfoForm value={draft} onChange={edit} live />
    </Dialog>
  );
}
